import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import locationService from '../../services/locationService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';

const DayBookReport = () => {
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [date, setDate] = useState(today);
  const [search, setSearch] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);

  const loadLocations = async () => {
    try {
      setLocationsLoading(true);
      const response = await locationService.list({ page: 0, size: 1000 });
      setLocations(response.content || []);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadReport = async (selectedDate = date) => {
    try {
      setLoading(true);
      const params = {
        date: selectedDate
      };

      if (locationId) {
        params.locationId = locationId;
      }
      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.DAY_BOOK_REPORT, { params });
      setRows(response.data || []);
      setPage(0);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load day book report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    loadReport(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today]);

  const handleGenerate = () => {
    loadReport(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalMoneyIn = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.moneyIn || 0), 0),
    [rows]
  );
  const totalMoneyOut = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.moneyOut || 0), 0),
    [rows]
  );
  const netMoney = totalMoneyIn - totalMoneyOut;

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ fontSize: 30, mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Day Book
            </Typography>
          </Box>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Firm / Location"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  <MenuItem value="">All Firms</MenuItem>
                  {locationsLoading ? (
                    <MenuItem disabled>Loading locations...</MenuItem>
                  ) : (
                    locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3.5}>
                <TextField
                  fullWidth
                  label="Search name / ref / type"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={22} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Ref</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Money In</TableCell>
                  <TableCell align="right">Money Out</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 3 }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No records found for selected date.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={`${row.source || 'row'}-${row.sourceId || index}-${index}`}>
                        <TableCell>{row.name || '-'}</TableCell>
                        <TableCell>{row.reference || '-'}</TableCell>
                        <TableCell>{row.type || '-'}</TableCell>
                        <TableCell>{row.paymentType || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.moneyIn)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.moneyOut)}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>

            {!loading && rows.length > 0 && (
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[15, 30, 50, 100]}
              />
            )}
          </TableContainer>

          {!loading && rows.length > 0 && (
            <Paper sx={{ mt: 1.5, p: 1.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: 'success.main', fontWeight: 700 }}>
                    Total Money-In: {formatCurrency(totalMoneyIn)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: 'error.main', fontWeight: 700, textAlign: { md: 'center' } }}>
                    Total Money-Out: {formatCurrency(totalMoneyOut)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      color: netMoney >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 700,
                      textAlign: { md: 'right' }
                    }}
                  >
                    Total Money In - Total Money Out: {formatCurrency(netMoney)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </Container>
    </Layout>
  );
};

export default DayBookReport;
